import * as React from 'react'
import { ScrollView, Text, View } from 'react-native'
import styled from '@theme/styled-components'

import Root from '@components/root/Root'
import Button from '@components/button/Button'
import Header from '@components/header/Header'
import TextInput from '@components/textInput/TextInput'
import SegmentedControl from '@components/segmentedControl/SegmentedControl'
import ManifestationCard from './ManifestationListCard'
import ManifestationWithStatCard from '@screens/home/ManifestationCard'
import ClosedManifestationsSection from './ClosedManifestationsSection'

import api from '@api/api'
import logger from '@utils/logger'

import useI18n from '@i18n/useI18n'
import useNavigation from '@layout/useNavigation'
import useBreakpoint from '@utils/useBreakpoints'
import { orderManifestations } from '@screens/manifestations/manifestationsUtils'
import useReducer from '@store/useReducer'
import * as SiteStore from '@store/site/site'

import { orderManifestationsByMonth } from '@screens/manifestations/manifestationsUtils'
import { firstLetterUppercase } from '@utils/stringUtils'
import sanitize from '@utils/Sanitizer'

import { createEvent, createStore } from 'effector'

const tabs = ['inProgress', 'incoming', 'toBeClosed'] as const
const setTab = createEvent<keyof OrderedManifestations>('setTab')
const TabStore = createStore<keyof OrderedManifestations>(tabs[0]).on(setTab, (_, next) => next)

const ManifestationsScreen = () => {
  const i18n = useI18n()
  const navigation = useNavigation()

  const site = useReducer(SiteStore.store, (s) => s.site!)
  const manifestations = useReducer(SiteStore.store, (s) => s.manifestations!)
  const tab = useReducer(TabStore, (s) => s)

  const [search, setSearch] = React.useState('')
  const [scrollHeight, setScrollHeight] = React.useState(0)
  const [stats, setStats] = React.useState<StatMapping>({})

  const TabOptions = tabs.map((t) => ({ label: i18n.t(`screens.manifestations.${t}`), value: t }))

  const [{ width }, currentBreakpoint] = useBreakpoint()
  const isPhone = currentBreakpoint === 'phone'

  const scroll = React.useRef<ScrollView>(null)

  React.useEffect(() => {
    api.manifestation
      .all(site.id)
      .then((res) => SiteStore.actions.setManifestations(orderManifestations(res)))
      .catch(logger.error)
  }, [])

  React.useEffect(() => {
    const isCurrentTabEmpty = !sections[tab].some((section) => section.manifestations.length > 0)

    if (isCurrentTabEmpty) {
      const newTab = tabs.find((t) => sections[t].some((section) => section.manifestations.length > 0))
      if (!!newTab) {
        setTab(newTab)
      }
    }
  }, [search])

  React.useEffect(() => {
    if (site) {
      const inProgress = sections[tab][0]

      Promise.all(inProgress.manifestations.map((m) => api.incident.stats(site.id, m.id)))
        .then((sts) => {
          const mapping = sts.reduce(
            (acc, cur, idx) => ({ ...acc, [inProgress.manifestations[idx].id]: cur }),
            {} as StatMapping
          )
          setStats(mapping)
        })
        .catch(logger.error)
    }
  }, [manifestations, site])

  const sections: OrederedManifestationSections = React.useMemo(() => {
    const inProgress: ManifestationsSection[] = [
      {
        section: '',
        manifestations: manifestations.inProgress.filter((m) => sanitize(m.libelle).includes(sanitize(search))),
      },
    ]

    const incoming: ManifestationsSection[] = orderManifestationsByMonth(
      manifestations.incoming.filter((m) => sanitize(m.libelle).includes(sanitize(search))),
      true
    )
    const toBeClosed: ManifestationsSection[] = orderManifestationsByMonth(
      manifestations.toBeClosed.filter((m) => sanitize(m.libelle).includes(sanitize(search))),
      true
    )

    return { inProgress, incoming, toBeClosed }
  }, [search])

  const navigateToManifestation = (manifestationId: string) => {
    navigation.push(`/manifestation/${manifestationId}`, { manifestationId: manifestationId })
  }

  const headerLinks = [
    {
      label: i18n.t(`screens.site.sites.${site.codeLieu}`),
      onClick: () => navigation.push('/'),
    },
    {
      label: i18n.t('screens.manifestations.title'),
      onClick: () => undefined,
    },
  ]

  const renderPhoneSearch = () => (
    <InputContainer>
      <TextInput
        searchStyle="onDark"
        value={search}
        onChange={setSearch}
        placeholder={i18n.t('screens.manifestations.placeholder')}
        left={{ name: 'search' }}
      />
    </InputContainer>
  )

  const renderBottomButton = () => (
    <BottomButton width={width}>
      <Button
        status="active"
        label={i18n.t('screens.site.startIncident')}
        onClick={() => navigation.push('/incident/start')}
      />
    </BottomButton>
  )

  const renderManifestation = (manifestation: ManifestationResponse, index: number) => {
    if (tab === 'inProgress') {
      return (
        <ManifestationContainer key={manifestation.id}>
          <ManifestationWithStatCard
            listWithStat={isPhone}
            manifestation={manifestation}
            onClick={() => navigateToManifestation(manifestation.id)}
            stats={stats[manifestation.id]}
          />
        </ManifestationContainer>
      )
    }

    return (
      <ManifestationContainer key={manifestation.id}>
        <ManifestationCard manifestation={manifestation} onClick={() => navigateToManifestation(manifestation.id)} />
      </ManifestationContainer>
    )
  }

  const renderMonthSection = (section: ManifestationsSection) => {
    const title = i18n.t('screens.manifestations.sectionDate', { date: new Date(section.section) })

    return (
      <View key={section.section}>
        <SectionDate>{firstLetterUppercase(title)}</SectionDate>
        <ManifestationList>{section.manifestations.map(renderManifestation)}</ManifestationList>
      </View>
    )
  }

  const renderInProgressManifestations = () => {
    const displayed = sections.inProgress[0]

    if (displayed.manifestations.length === 0) {
      return <MissingInfo>{i18n.t('screens.manifestations.noManifestation')}</MissingInfo>
    }

    return <ManifestationList>{displayed.manifestations.map(renderManifestation)}</ManifestationList>
  }

  const renderIncomingManifestations = () => {
    if (sections.incoming.length === 0) {
      return <MissingInfo>{i18n.t('screens.manifestations.noManifestation')}</MissingInfo>
    }

    return sections.incoming.map(renderMonthSection)
  }

  const renderToBeClosedManifestations = () => {
    if (sections.toBeClosed.length === 0) {
      return <MissingInfo>{i18n.t('screens.manifestations.noManifestation')}</MissingInfo>
    }

    if (isPhone) {
      return <ClosedManifestationsSection scroll={scroll} scrollHeight={scrollHeight} sections={sections.toBeClosed} />
    }

    return sections.toBeClosed.map(renderMonthSection)
  }

  return (
    <Root
      header={i18n.t('screens.manifestations.title')}
      renderFixedBottom={isPhone ? renderBottomButton : undefined}
      renderSubHeader={renderPhoneSearch}
      onLeft={navigation.goBack}
      fixed>
      <ScrollContainer
        scrollIndicatorInsets={{ right: 1 }}
        bounces={false}
        keyboardShouldPersistTaps="handled"
        ref={scroll}
        onLayout={(e) => setScrollHeight(e.nativeEvent.layout.height)}>
        {!isPhone && (
          <Header
            siteIcon={site.codeLieu as SiteIconName}
            title={i18n.t('screens.manifestations.title')}
            links={headerLinks}
            buttons={[
              {
                label: i18n.t('screens.site.startIncident'),
                onClick: () => navigation.push('/incident/start'),
              },
            ]}
          />
        )}
        <MainContainer>
          {!isPhone && (
            <TextInput
              value={search}
              onChange={setSearch}
              placeholder={i18n.t('screens.manifestations.placeholderLong')}
              left={{ name: 'search' }}
            />
          )}

          <SegmentedContainer>
            <SegmentedControl
              data={TabOptions}
              value={TabOptions.find((t) => t.value === tab)}
              onItemSelected={(opt) => setTab(opt.value)}
            />
          </SegmentedContainer>

          {tab === 'inProgress'
            ? renderInProgressManifestations()
            : tab === 'incoming'
            ? renderIncomingManifestations()
            : renderToBeClosedManifestations()}
        </MainContainer>
      </ScrollContainer>
    </Root>
  )
}

export default ManifestationsScreen

const MainContainer = styled(View)`
  padding: ${(props) => (props.theme.breakpoint === 'phone' ? `16px` : '24px')};
`

const InputContainer = styled(View)`
  margin-top: 16px;
`

const SegmentedContainer = styled(View)`
  ${(props) => (props.theme.breakpoint === 'phone' ? `margin-bottom: 16px;` : `margin: 24px 0px;`)}
`

const BottomButton = styled(View)<{ width: number }>`
  width: ${(props) => props.width}px;
  background-color: ${(props) => props.theme.colors.background}E6;
  padding: 16px;
`

const ManifestationList = styled(View)`
  flex-direction: ${(props) => (props.theme.breakpoint === 'phone' ? 'column' : 'row')};
  ${(props) => props.theme.breakpoint !== 'phone' && `flex-wrap: wrap;`}
`

const ManifestationContainer = styled(View)`
  ${(props) => (props.theme.breakpoint === 'phone' ? 'margin-bottom: 8px;' : 'margin: 0px 16px 16px 0px;')}
`

const MissingInfo = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  font-size: 14px;
  color: ${(props) => props.theme.colors.secondaryText};
  ${(props) =>
    props.theme.breakpoint === 'phone'
      ? ` padding: 23px 16px;
          background-color: ${props.theme.colors.background};
          text-align: center;
          font-size: 12px;`
      : `padding: 16px 0px;`}
`

const SectionDate = styled(Text)`
  ${(props) => (props.theme.breakpoint === 'phone' ? props.theme.fonts.Caption2 : props.theme.fonts.Body)};
  color: ${(props) => props.theme.colors.secondaryText};
  margin-bottom: 8px;
`

const ScrollContainer = styled(ScrollView)`
  flex: 1;
  flex-direction: column;
`
